import { useEffect, useRef, useState } from 'react';

import { type IAssignedAgent } from '../../../../interface/agent-assignment';
import { type ICitizensSitrepContext, type ICitizensSitrepItem, type ICoreReportItem } from '../../../../interface/child-agent';
import {
	canQueryCitizensSitrep,
	filterReportsByUser,
	getCitizensSituationReports,
	getCoreReportsForAgent,
} from '../../../../lib/child-sitrep-api';
import Lightbox from '../Lightbox';
import { relativeTime } from '../utils';
import TabMessage from './TabMessage';

type ViewState = 'loading' | 'ready' | 'error';

interface IUnifiedReport {
	id: string;
	source: 'incident' | 'citizen';
	title: string;
	body?: string;
	createdAt?: string;
	media: string[];
	resolved?: boolean;
	lat?: string | number;
	lng?: string | number;
}

const MegaphoneIcon = () => (
	<svg
		viewBox='0 0 24 24'
		fill='none'
		aria-hidden='true'
	>
		<path
			d='M4 9v6h3l8 4V5L7 9H4Zm13 .5a3 3 0 0 1 0 5'
			stroke='currentColor'
			strokeWidth='1.6'
			strokeLinecap='round'
			strokeLinejoin='round'
		/>
	</svg>
);
const WarnGlyph = () => (
	<svg
		viewBox='0 0 20 20'
		fill='none'
		aria-hidden='true'
	>
		<path
			d='M10 6.7v3.6M10 13.4h.01M8.6 2.6 1.7 14.5c-.6 1 .1 2.3 1.4 2.3h13.8c1.3 0 2-1.3 1.4-2.3L11.4 2.6c-.6-1.1-2.2-1.1-2.8 0Z'
			stroke='currentColor'
			strokeWidth='1.6'
			strokeLinecap='round'
			strokeLinejoin='round'
		/>
	</svg>
);

const toUnifiedIncident = (r: ICoreReportItem): IUnifiedReport => ({
	id: `incident-${r.id}`,
	source: 'incident',
	title: r.report?.title || 'Incident report',
	body: r.report?.body,
	createdAt: r.createdAt,
	media: [],
	resolved: r.isResolved,
});

const toUnifiedCitizen = (r: ICitizensSitrepItem): IUnifiedReport => ({
	id: `citizen-${r.id}`,
	source: 'citizen',
	title: r.situation || 'Situation report',
	createdAt: r.createdAt,
	media: (r.mediaUrl ?? []).filter(Boolean),
	lat: r.latitude,
	lng: r.longitude,
});

interface ISitrepTabProps {
	agent: IAssignedAgent | null;
	sitrepCtx?: Partial<ICitizensSitrepContext> | null;
	onCount: (count: number) => void;
}

/**
 * Tab 3 — situation reports published by the child, drawn from BOTH sources:
 * (a) Core incident reports filed by the agent (always available, filtered
 * client-side on userId) and (b) citizens-agent situation reports for the
 * entity (only when the hierarchy context is available — otherwise degrades to
 * (a) with a footnote).
 */
const SitrepTab = ({ agent, sitrepCtx, onCount }: ISitrepTabProps) => {
	const [state, setState] = useState<ViewState>('loading');
	const [reports, setReports] = useState<IUnifiedReport[]>([]);
	const [citizenDegraded, setCitizenDegraded] = useState(false);
	const [lightbox, setLightbox] = useState<{ images: string[]; index: number } | null>(null);
	const loadedRef = useRef(false);

	const load = () => {
		setState('loading');
		const canCitizen = canQueryCitizensSitrep(sitrepCtx);
		setCitizenDegraded(!canCitizen);

		const incidentP = getCoreReportsForAgent();
		const citizenP = canCitizen ? getCitizensSituationReports(sitrepCtx) : Promise.resolve(null);

		Promise.all([incidentP, citizenP]).then(([incidentOut, citizenOut]) => {
			const incidentReachable = incidentOut.reachable;
			const incidents =
				incidentOut.reachable && incidentOut.httpStatus >= 200 && incidentOut.httpStatus < 300
					? filterReportsByUser(incidentOut.items, agent?.userId).map(toUnifiedIncident)
					: [];

			const citizens =
				citizenOut && citizenOut.reachable && citizenOut.httpStatus >= 200 && citizenOut.httpStatus < 300
					? citizenOut.items.map(toUnifiedCitizen)
					: [];

			// Only a hard failure when the always-on incident source was unreachable.
			if (!incidentReachable && citizens.length === 0) {
				setState('error');
				return;
			}

			const merged = [...incidents, ...citizens].sort(
				(a, b) => new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime()
			);
			setReports(merged);
			onCount(merged.length);
			setState('ready');
		});
	};

	useEffect(() => {
		if (loadedRef.current) return;
		loadedRef.current = true;
		load();
	}, []);

	if (state === 'loading') {
		return (
			<div className='ca-sitrep__skeleton f-column-15'>
				<div className='ca-sitrep__sk-card skeleton' />
				<div className='ca-sitrep__sk-card skeleton' />
			</div>
		);
	}

	if (state === 'error') {
		return (
			<TabMessage
				icon={<WarnGlyph />}
				tone='danger'
				title='We couldn’t load situation reports.'
				hint='The reports service is temporarily unavailable.'
				action={{ label: 'Try again', onClick: load }}
			/>
		);
	}

	if (!reports.length) {
		return (
			<TabMessage
				icon={<MegaphoneIcon />}
				tone='neutral'
				title='No situation reports yet'
				hint='This agent has not published any field reports for this entity.'
			/>
		);
	}

	return (
		<div className='ca-sitrep f-column-15'>
			{reports.map((r) => (
				<article
					className='ca-sitrep__card'
					key={r.id}
				>
					<div className='ca-sitrep__card-head'>
						<span className={`ca-sitrep__source ca-sitrep__source--${r.source}`}>
							{r.source === 'incident' ? 'Incident' : 'Citizen'}
						</span>
						{r.createdAt ? <span className='ca-sitrep__time'>{relativeTime(r.createdAt)}</span> : null}
						{r.resolved ? <span className='ca-sitrep__resolved'>Resolved</span> : null}
					</div>
					<p className='ca-sitrep__title'>{r.title}</p>
					{r.body ? <p className='ca-sitrep__body'>{r.body}</p> : null}
					{r.media.length ? (
						<div className='ca-sitrep__media'>
							{r.media.map((url, i) => (
								<button
									type='button'
									className='ca-sitrep__thumb'
									key={url}
									onClick={() => setLightbox({ images: r.media, index: i })}
									aria-label={`Open report photo ${i + 1}`}
								>
									<img
										src={url}
										alt={`Report ${i + 1}`}
										loading='lazy'
									/>
								</button>
							))}
						</div>
					) : null}
					{r.lat && r.lng ? (
						<a
							className='ca-sitrep__map-link'
							href={`https://www.openstreetmap.org/?mlat=${r.lat}&mlon=${r.lng}#map=16/${r.lat}/${r.lng}`}
							target='_blank'
							rel='noopener noreferrer'
						>
							View on map
						</a>
					) : null}
				</article>
			))}

			{citizenDegraded ? (
				<p className='ca-sitrep__footnote'>Citizen-submitted reports for this unit aren’t available in this view yet.</p>
			) : null}

			{lightbox ? (
				<Lightbox
					images={lightbox.images}
					startIndex={lightbox.index}
					label='Report'
					onClose={() => setLightbox(null)}
				/>
			) : null}
		</div>
	);
};

export default SitrepTab;
